import { useRef } from "react";
import { Star, ShieldCheck, Linkedin, MapPin, Sparkles, ArrowRight } from "lucide-react";
import { LocalizedLink as Link } from "@/components/routing/LocalizedLink";
import DemoBadge from "@/components/shared/DemoBadge";
import { useTranslation } from "react-i18next";
import { TierBadge, type ProviderTier } from "@/components/admin/TierBadge";
import { TrustBadge, type TrustStatus } from "@/components/trust/TrustBadge";
import { AspectRatio } from "@/components/ui/aspect-ratio";

export type ProviderVariant = "coach" | "executive" | "mentor";

export type ProviderCardData = {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  headline: string | null;
  expertise_areas: string[] | null;
  languages: string[] | null;
  rating_avg: number | null;
  rating_count: number | null;
  hourly_eur?: number | null;
  hourly_xof?: number | null;
  demo?: boolean;
  city?: string | null;
  is_new?: boolean;
  variant?: ProviderVariant;
  bootpreneurs_badge?: boolean;
  tier?: ProviderTier | null;
  trust_status?: TrustStatus | null;
  photo_url?: string | null;
  intro_video_url?: string | null;
  linkedin_url?: string | null;
  featured?: boolean;
  featured_until?: string | null;
  price_unit?: "hour" | "month";
  price_eur_max?: number | null;
  price_xof_max?: number | null;
  detail_href?: string;
  role_label?: string | null;
};

const NEW_LABELS: Record<ProviderVariant, { en: string; fr: string }> = {
  coach: { en: "New coach", fr: "Nouveau coach" },
  executive: { en: "New executive", fr: "Nouvel exécutif" },
  mentor: { en: "New mentor", fr: "Nouveau mentor" },
};

export function ProviderCard({ p }: { p: ProviderCardData }) {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "en").startsWith("fr") ? "fr" : "en";
  const initials = `${p.first_name?.[0] ?? ""}${p.last_name?.[0] ?? ""}`.toUpperCase() || "?";
  const showNewPill = (p.is_new === true || p.rating_avg == null) && !!p.variant;
  const videoRef = useRef<HTMLVideoElement>(null);
  const photo = p.photo_url || p.avatar_url || null;
  const fullName = `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim();
  const isFeatured = p.featured === true;
  const bookLabel = lang === "fr" ? "Réserver" : "Book intro";
  const fromLabel = lang === "fr" ? "À partir de" : "From";
  const unitSuffix = p.price_unit === "month" ? (lang === "fr" ? "/mois" : "/mo") : "/h";
  const href = p.detail_href ?? `/services/coaching/${p.user_id}`;
  return (
    <Link
      to={href}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-all duration-base ease-brand hover:-translate-y-0.5 hover:shadow-lg hover:border-primary/40"
      onMouseEnter={() => videoRef.current?.play().catch(() => {})}
      onMouseLeave={() => { const v = videoRef.current; if (v) { v.pause(); v.currentTime = 0; } }}
    >
      {/* Media */}
      <div className="relative w-full overflow-hidden bg-muted">
        <AspectRatio ratio={16 / 10}>
          {p.intro_video_url ? (
            <video
              ref={videoRef}
              src={p.intro_video_url}
              poster={photo ?? undefined}
              muted
              playsInline
              loop
              preload="metadata"
              className="h-full w-full object-cover transition-transform duration-500 ease-brand group-hover:scale-[1.03]"
            />
          ) : photo ? (
            <img
              src={photo}
              alt={fullName || "Provider"}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-500 ease-brand group-hover:scale-[1.03]"
            />
          ) : (
            <div
              className="flex h-full w-full items-center justify-center text-4xl font-extrabold text-white"
              style={{ background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--accent)))" }}
            >
              {initials}
            </div>
          )}
        </AspectRatio>

        {/* Left-stack badges */}
        <div className="absolute left-3 top-3 z-[2] flex flex-col items-start gap-1.5">
          {isFeatured && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/95 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-md backdrop-blur-sm">
              <Sparkles className="h-3 w-3" />
              {lang === "fr" ? "Sélectionné" : "Featured"}
            </span>
          )}
          {showNewPill && !isFeatured && (
            <span className="inline-flex items-center rounded-full bg-primary/95 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-primary-foreground shadow-sm backdrop-blur-sm">
              {NEW_LABELS[p.variant!][lang]}
            </span>
          )}
          {p.bootpreneurs_badge && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/95 px-2 py-[3px] text-[10px] font-bold uppercase tracking-[0.08em] text-primary-foreground shadow-sm">
              <ShieldCheck className="h-3 w-3" />
              Bootpreneurs
            </span>
          )}
        </div>

        {/* Right-stack badges */}
        <div className="absolute right-3 top-3 z-[2] flex flex-col items-end gap-1.5">
          {p.demo && <DemoBadge lang={lang} />}
          {p.linkedin_url && (
            <a
              href={p.linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`LinkedIn profile of ${fullName || "provider"}`}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-background/95 text-primary shadow-sm transition hover:bg-primary hover:text-primary-foreground"
            >
              <Linkedin className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col gap-2.5 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="truncate text-base font-bold text-foreground">{fullName || "—"}</h3>
            {p.role_label && (
              <div className="mt-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-primary">{p.role_label}</div>
            )}
            {p.headline && (
              <p className="mt-0.5 line-clamp-2 text-sm leading-snug text-muted-foreground">{p.headline}</p>
            )}
          </div>
        </div>

        {(p.tier || p.trust_status) && (
          <div className="flex flex-wrap items-center gap-1">
            <TrustBadge status={p.trust_status} size="sm" />
            <TierBadge tier={p.tier} trustStatus={p.trust_status} />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {p.city && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {p.city}
            </span>
          )}
          {p.rating_avg != null && (
            <span className="inline-flex items-center gap-1">
              <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
              <span className="font-semibold text-foreground">{Number(p.rating_avg).toFixed(1)}</span>
              <span>({p.rating_count ?? 0})</span>
            </span>
          )}
        </div>

        {!!(p.expertise_areas ?? []).length && (
          <div className="flex flex-wrap gap-1">
            {(p.expertise_areas ?? []).slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        <div className="mt-auto flex items-end justify-between border-t border-border pt-3">
          <div>
            {p.hourly_eur != null ? (
              <>
                <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{fromLabel}</div>
                <div className="text-base font-bold leading-tight text-foreground">
                  €{p.hourly_eur.toLocaleString("fr-FR")}
                  {p.price_eur_max != null && p.price_eur_max !== p.hourly_eur ? `–€${p.price_eur_max.toLocaleString("fr-FR")}` : ""}
                  {unitSuffix}
                </div>
                {p.hourly_xof != null && (
                  <div className="text-[10px] leading-tight text-muted-foreground">
                    ≈ {p.hourly_xof.toLocaleString("fr-FR")}
                    {p.price_xof_max != null && p.price_xof_max !== p.hourly_xof ? `–${p.price_xof_max.toLocaleString("fr-FR")}` : ""} XOF{unitSuffix}
                  </div>
                )}
              </>
            ) : (
              <span className="text-xs text-muted-foreground">{lang === "fr" ? "Sur devis" : "On request"}</span>
            )}
          </div>
          <span className="inline-flex items-center gap-1 rounded-full border border-primary/30 px-3 py-1.5 text-xs font-semibold text-primary transition group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary">
            {bookLabel}
            <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}
